import React, { useState, useEffect } from 'react';
import { FaGamepad, FaTrophy, FaUsers, FaClock, FaSearch, FaFilter, FaPlus, FaChess, FaClipboardList, FaUserFriends } from 'react-icons/fa';
import { apiService } from '../services/apiService';
import { useNavigate } from 'react-router-dom';
import CreateChallengeModal from '../components/CreateChallengeModal';

interface P2PGame {
  id: number;
  name: string;
  type: string;
  minStake: number;
  maxStake: number;
  description: string;
  rules: string;
  avgDuration: string;
  isEnabled: boolean;
  activePlayers: number;
}

const P2PGames: React.FC = () => {
  const navigate = useNavigate();
  const [games, setGames] = useState<P2PGame[]>([]);
  const [filteredGames, setFilteredGames] = useState<P2PGame[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedType, setSelectedType] = useState<string>('ALL');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [selectedGame, setSelectedGame] = useState<P2PGame | null>(null);

  useEffect(() => {
    fetchGames();
  }, []);

  useEffect(() => {
    filterGames();
  }, [games, searchQuery, selectedType]);

  const fetchGames = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await apiService.get('/api/p2p-games/list');
      if (response.success) {
        setGames(response.data);
      } else {
        setError('Failed to load games');
      }
    } catch (err: any) {
      console.error('Error fetching P2P games:', err);
      setError(err.message || 'Failed to load games');
    } finally {
      setLoading(false);
    }
  };

  const filterGames = () => {
    let filtered = [...games];

    // Filter by search query
    if (searchQuery) {
      filtered = filtered.filter(game =>
        game.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        game.description.toLowerCase().includes(searchQuery.toLowerCase())
      );
    }

    // Filter by type
    if (selectedType !== 'ALL') {
      filtered = filtered.filter(game => game.type === selectedType);
    }

    setFilteredGames(filtered);
  };

  const getGameIcon = (type: string) => {
    switch (type) {
      case 'STRATEGY':
        return <FaChess />;
      case 'QUIZ':
        return <FaClipboardList />;
      case 'ARCADE':
        return <FaGamepad />;
      default:
        return <FaGamepad />;
    }
  };

  const getTypeColor = (type: string) => {
    switch (type) {
      case 'STRATEGY':
        return '#667eea';
      case 'CARD':
        return '#f56565';
      case 'QUIZ':
        return '#48bb78';
      case 'ARCADE':
        return '#ed8936';
      default:
        return '#4299e1';
    }
  };

  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return (
    <div style={{ 
      maxWidth: 1400, 
      margin: '0 auto', 
      padding: isMobile ? '16px' : '24px',
      width: '100%',
      boxSizing: 'border-box',
      overflowX: 'hidden'
    }}>
      {/* Header */}
      <div style={{ marginBottom: 32 }}>
        <div style={{ 
          display: 'flex', 
          alignItems: isMobile ? 'flex-start' : 'center', 
          justifyContent: 'space-between', 
          marginBottom: 16,
          flexDirection: isMobile ? 'column' : 'row',
          gap: isMobile ? 16 : 0
        }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <h1 style={{ 
              fontSize: isMobile ? 24 : 32, 
              fontWeight: 700, 
              color: '#1a202c', 
              marginBottom: 8, 
              display: 'flex', 
              alignItems: 'center', 
              gap: 12,
              flexWrap: 'wrap'
            }}>
              <FaTrophy style={{ color: '#667eea' }} />
              P2P Game Challenges
            </h1>
            <p style={{ fontSize: isMobile ? 14 : 16, color: '#718096' }}>
              Challenge other players to skill-based games and win real money!
            </p>
          </div>
          <div style={{ 
            display: 'flex', 
            gap: isMobile ? 8 : 12,
            flexWrap: isMobile ? 'wrap' : 'nowrap'
          }}>
            <button
              onClick={() => navigate('/p2p-games/challenges')}
              style={{
                padding: isMobile ? '10px 16px' : '12px 24px',
                background: '#667eea',
                color: 'white',
                border: 'none',
                borderRadius: 8,
                fontSize: isMobile ? 12 : 14,
                fontWeight: 600,
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                transition: 'all 0.3s ease',
                whiteSpace: 'nowrap',
                flexShrink: 0
              }}
              onMouseEnter={e => e.currentTarget.style.background = '#5568d3'}
              onMouseLeave={e => e.currentTarget.style.background = '#667eea'}
            >
              <FaClipboardList />
              {isMobile ? 'Challenges' : 'My Challenges'}
            </button>
            <button
              onClick={() => navigate('/friends')}
              style={{
                padding: isMobile ? '10px 16px' : '12px 24px',
                background: '#48bb78',
                color: 'white',
                border: 'none',
                borderRadius: 8,
                fontSize: isMobile ? 12 : 14,
                fontWeight: 600,
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                transition: 'all 0.3s ease',
                whiteSpace: 'nowrap',
                flexShrink: 0
              }}
              onMouseEnter={e => e.currentTarget.style.background = '#38a169'}
              onMouseLeave={e => e.currentTarget.style.background = '#48bb78'}
            >
              <FaUsers />
              {isMobile ? 'Friends' : 'My Friends'}
            </button>
          </div>
        </div>

        {/* Search and Filter Bar */}
        <div style={{ 
          display: 'flex', 
          gap: isMobile ? 12 : 16, 
          flexWrap: 'wrap',
          width: '100%'
        }}>
          {/* Search */}
          <div style={{ 
            flex: 1, 
            minWidth: isMobile ? '100%' : 250, 
            position: 'relative',
            width: '100%'
          }}>
            <FaSearch style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: '#a0aec0' }} />
            <input
              type="text"
              placeholder="Search games..."
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              style={{
                width: '100%',
                padding: '12px 12px 12px 40px',
                border: '1px solid #e2e8f0',
                borderRadius: 8,
                fontSize: 14,
                outline: 'none',
                boxSizing: 'border-box'
              }}
            />
          </div>

          {/* Type Filter */}
          <div style={{ 
            display: 'flex', 
            gap: isMobile ? 6 : 8,
            flexWrap: 'wrap',
            width: isMobile ? '100%' : 'auto'
          }}>
            {['ALL', 'STRATEGY', 'CARD', 'QUIZ', 'ARCADE'].map(type => (
              <button
                key={type}
                onClick={() => setSelectedType(type)}
                style={{
                  padding: isMobile ? '10px 14px' : '12px 20px',
                  background: selectedType === type ? '#667eea' : 'white',
                  color: selectedType === type ? 'white' : '#4a5568',
                  border: '1px solid #e2e8f0',
                  borderRadius: 8,
                  fontSize: isMobile ? 12 : 14,
                  fontWeight: 500,
                  cursor: 'pointer',
                  transition: 'all 0.3s ease',
                  whiteSpace: 'nowrap',
                  flex: isMobile ? '1 1 auto' : '0 0 auto',
                  minWidth: isMobile ? 'auto' : 'auto'
                }}
              >
                {type}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Error State */}
      {error && (
        <div style={{
          padding: 16,
          background: '#fed7d7',
          borderRadius: 8,
          color: '#c53030',
          marginBottom: 24,
          display: 'flex',
          alignItems: 'center',
          gap: 12
        }}>
          <span>⚠️</span>
          <span>{error}</span>
        </div>
      )}

      {/* Loading State */}
      {loading && (
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', padding: 60 }}>
          <div style={{
            width: 50,
            height: 50,
            border: '4px solid #e2e8f0',
            borderTop: '4px solid #667eea',
            borderRadius: '50%',
            animation: 'spin 1s linear infinite'
          }} />
        </div>
      )}

      {/* Game Grid */}
      {!loading && !error && (
        <div>
          {filteredGames.length === 0 ? (
            <div style={{
              textAlign: 'center',
              padding: 60,
              background: 'white',
              borderRadius: 12,
              border: '1px solid #e2e8f0'
            }}>
              <FaGamepad style={{ fontSize: 48, color: '#cbd5e0', marginBottom: 16 }} />
              <h3 style={{ fontSize: 18, fontWeight: 600, color: '#4a5568', marginBottom: 8 }}>
                No games found
              </h3>
              <p style={{ color: '#718096' }}>
                Try adjusting your search or filters
              </p>
            </div>
          ) : (
            <div style={{
              display: 'grid',
              gridTemplateColumns: isMobile 
                ? '1fr' 
                : 'repeat(auto-fill, minmax(300px, 1fr))',
              gap: isMobile ? 16 : 24,
              width: '100%'
            }}>
              {filteredGames.map(game => (
                <div
                  key={game.id}
                  style={{
                    background: 'white',
                    borderRadius: 12,
                    border: '1px solid #e2e8f0',
                    overflow: 'hidden',
                    transition: 'all 0.3s ease',
                    cursor: 'pointer'
                  }}
                  onMouseEnter={e => {
                    e.currentTarget.style.transform = 'translateY(-4px)';
                    e.currentTarget.style.boxShadow = '0 10px 25px rgba(0,0,0,0.1)';
                  }}
                  onMouseLeave={e => {
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.boxShadow = 'none';
                  }}
                  onClick={() => {
                    setSelectedGame(game);
                    setShowCreateModal(true);
                  }}
                >
                  {/* Game Header */}
                  <div style={{
                    padding: 20,
                    background: `linear-gradient(135deg, ${getTypeColor(game.type)} 0%, ${getTypeColor(game.type)}dd 100%)`,
                    color: 'white'
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                        <div style={{ fontSize: 32 }}>
                          {getGameIcon(game.type)}
                        </div>
                        <div>
                          <h3 style={{ fontSize: 20, fontWeight: 700, marginBottom: 4 }}>
                            {game.name}
                          </h3>
                          <span style={{
                            fontSize: 12,
                            background: 'rgba(255,255,255,0.25)',
                            padding: '4px 8px',
                            borderRadius: 4,
                            fontWeight: 600
                          }}>
                            {game.type}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Game Body */}
                  <div style={{ padding: 20 }}>
                    <p style={{ fontSize: 14, color: '#4a5568', marginBottom: 20, minHeight: 40 }}>
                      {game.description}
                    </p>

                    {/* Game Stats */}
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 20 }}>
                      <div style={{
                        padding: 12,
                        background: '#f7fafc',
                        borderRadius: 8,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 8
                      }}>
                        <FaUsers style={{ color: '#667eea' }} />
                        <div>
                          <div style={{ fontSize: 18, fontWeight: 700, color: '#1a202c' }}>
                            {game.activePlayers}
                          </div>
                          <div style={{ fontSize: 11, color: '#718096' }}>
                            Active Players
                          </div>
                        </div>
                      </div>

                      <div style={{
                        padding: 12,
                        background: '#f7fafc',
                        borderRadius: 8,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 8
                      }}>
                        <FaClock style={{ color: '#667eea' }} />
                        <div>
                          <div style={{ fontSize: 18, fontWeight: 700, color: '#1a202c' }}>
                            {game.avgDuration}
                          </div>
                          <div style={{ fontSize: 11, color: '#718096' }}>
                            Avg Duration
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Stake Range */}
                    <div style={{
                      padding: 12,
                      background: '#f0fff4',
                      border: '1px solid #9ae6b4',
                      borderRadius: 8,
                      marginBottom: 16
                    }}>
                      <div style={{ fontSize: 12, color: '#22543d', fontWeight: 600, marginBottom: 4 }}>
                        Stake Range
                      </div>
                      <div style={{ fontSize: 18, fontWeight: 700, color: '#22543d' }}>
                        €{game.minStake.toFixed(2)} - €{game.maxStake.toFixed(2)}
                      </div>
                    </div>

                    {/* Challenge Button */}
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setSelectedGame(game);
                        setShowCreateModal(true);
                      }}
                      style={{
                        width: '100%',
                        padding: '12px 20px',
                        background: getTypeColor(game.type),
                        color: 'white',
                        border: 'none',
                        borderRadius: 8,
                        fontSize: 14,
                        fontWeight: 600,
                        cursor: 'pointer',
                        transition: 'all 0.3s ease',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: 8
                      }}
                      onMouseEnter={e => e.currentTarget.style.opacity = '0.9'}
                      onMouseLeave={e => e.currentTarget.style.opacity = '1'}
                    >
                      <FaPlus />
                      Create Challenge
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Create Challenge Modal */}
      {showCreateModal && selectedGame && (
        <CreateChallengeModal
          game={selectedGame}
          onClose={() => {
            setShowCreateModal(false);
            setSelectedGame(null);
          }}
          onSuccess={(challengeId) => {
            setShowCreateModal(false);
            setSelectedGame(null);
            navigate('/p2p-games/challenges');
          }}
        />
      )}

      <style>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
};

export default P2PGames;

